// @file Surface share links store
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { authWithReferrerUrl, currentUrl, getSearchParam, navigateTo, transformUrl } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import PromiseQueue from '@@/bits/promise_queue'
import {
  getSectionBreakoutDisabledUrl,
  getSectionBreakoutPostUrl,
  getSubmissionRequestConfirmationUrl,
  getSubmissionRequestDisabledUrl,
} from '@@/bits/surface_share_links_helper'
import { isRegistered } from '@@/bits/user_model'
import { SnackbarNotificationType } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type {
  Cid,
  Id,
  PostAttributes,
  WallSectionBreakoutLink,
  WallSubmissionRequestLink,
  WallSubmissionRequestProps,
} from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { GroupByTypes, saveWall } from '@padlet/arvo'
import { CONFIRM_TURN_OFF } from 'bits/confirmation_dialog'
import { keyBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'
import { useCopyToClipboard } from 'vuecomposables/copy_to_clipboard'

// replication of options in WallShareLinks::SubmissionRequest::RedirectLocations
export enum SubmissionRequestRedirectLocation {
  Padlet = 'padlet',
  Confirmation = 'confirmation',
  RequestAgain = 'request_again',
}

export const useSurfaceShareLinksStore = defineStore('surfaceShareLinks', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceMapStore = useSurfaceMapStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { genericFetchError } = useGlobalSnackbarStore()
  const queue = new PromiseQueue()

  /* Section Breakout */
  // State
  const isEnablingSectionBreakout = ref(false)
  const isSectionBreakoutEnabled = ref<boolean>(false)
  const sectionBreakoutLinks = ref<Record<number, WallSectionBreakoutLink> | null>(null)
  const disableSectionBreakoutTimer = ref<ReturnType<typeof setTimeout> | undefined>()
  const disableSectionBreakoutSnackbarUid = ref<string | null>(null)

  // Getters
  const sectionBreakoutIdentifier = computed<string | null>(() => surfaceVuexStore?.getters.sectionBreakoutIdentifier)

  // Actions

  async function enableGroupBySections(): Promise<void> {
    try {
      await saveWall({
        ...surfaceVuexStore?.state.wall,
        wish_arrangement: {
          group_by: GroupByTypes.Section,
          is_sectionable: true,
        },
      })
    } catch (e) {
      genericFetchError()
      captureFetchException(e)
    }
  }

  function setSectionBreakoutLinksState(links: WallSectionBreakoutLink[] | null): void {
    if (links !== null) {
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } else {
      isSectionBreakoutEnabled.value = false
      sectionBreakoutLinks.value = null
    }
  }

  async function fetchSectionBreakoutLinks(): Promise<void> {
    if (
      !isAppUsing('sectionBreakout') ||
      surfaceStore.isSectionBreakout ||
      surfaceStore.isSubmissionRequest ||
      !surfacePermissionsStore.canIAdminister
    ) {
      // early exit if not supposed to show section breakout links
      return
    }
    try {
      const links = await PadletApi.SectionBreakout.list(surfaceStore.wallId)
      if (links == null) {
        setSectionBreakoutLinksState(null)
        return
      }
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelListSectionBreakoutLinks' })
    }
  }

  async function enableSectionBreakout(): Promise<void> {
    // Clear any existing callbacks to disable
    if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
    globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')

    const links = await PadletApi.SectionBreakout.enable(surfaceStore.wallId)
    setSectionBreakoutLinksState(links)
  }

  async function disableSectionBreakout(): Promise<void> {
    try {
      await PadletApi.SectionBreakout.disable(surfaceStore.wallId)
      setSectionBreakoutLinksState(null)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelDisableSectionBreakout' })
    }
  }

  async function disableSectionBreakoutWithUndo(): Promise<void> {
    const SNACKBAR_DURATION = 3000

    disableSectionBreakoutSnackbarUid.value = globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Breakout links closed'),
      actionText: 'Undo',
      persist: true,
      actionTextActions: [
        () => {
          // revert and close snackbar
          isSectionBreakoutEnabled.value = true
          if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
          globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
        },
      ],
    })

    // Hide section breakout first to give illusion of deletion
    isSectionBreakoutEnabled.value = false

    // Timeout to close snackbar and disable section breakout links
    disableSectionBreakoutTimer.value = setTimeout(() => {
      globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
      void disableSectionBreakout()
    }, SNACKBAR_DURATION)
  }

  function askToDisableSectionBreakout(): void {
    void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
      ...CONFIRM_TURN_OFF,
      title: __('Turn off breakout links?'),
      body: __('Current links will no longer work. Turning links back on will create new links.'),
      afterConfirmActions: [disableSectionBreakoutWithUndo],
    })
  }

  async function copySectionBreakoutLinkAndAlert(sectionId: number): Promise<void> {
    if (sectionBreakoutLinks.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: sectionBreakoutLinks.value[sectionId].link,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  async function copySectionBreakoutPostLinkAndAlert(postId: number): Promise<void> {
    if (sectionBreakoutIdentifier.value !== null) {
      const sectionBreakoutPostUrl = getSectionBreakoutPostUrl(
        surfaceStore.namespace,
        sectionBreakoutIdentifier.value,
        postId,
      )
      await useCopyToClipboard().copyToClipboard({
        text: sectionBreakoutPostUrl,
        globalSnackbarOptions: {
          message: __('Link copied to clipboard'),
          notificationType: SnackbarNotificationType.success,
        },
      })
    }
  }

  async function navigateToSectionBreakoutDisabledPage(): Promise<void> {
    if (sectionBreakoutIdentifier.value !== null) {
      navigateTo(getSectionBreakoutDisabledUrl())
    }
  }

  async function toggleSectionBreakout(): Promise<void> {
    const originalValue = isSectionBreakoutEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSectionBreakout.value) return

      if (isSectionBreakoutEnabled.value) {
        askToDisableSectionBreakout()
      } else {
        // Only show loading state if toggling to True
        isEnablingSectionBreakout.value = true
        await enableSectionBreakout()
        isEnablingSectionBreakout.value = false
      }
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSectionBreakout' })
      isSectionBreakoutEnabled.value = originalValue
    }
  }

  function showSectionInputDialog(): void {
    const title = __('Section %{number}', {
      number: ((surfaceVuexStore?.getters['section/numOfSections'] ?? 0) as number) + 1,
    })

    const payload = {
      title: __('New section'),
      inputValue: title,
      shouldFadeIn: true,
      inputPlaceholder: 'Section title',
      inputAriaLabel: __('Section title'),
      submitButtonText: 'Create',
      submitActions: [
        async ({ inputValue }) => {
          await surfaceSectionsStore.createSectionWithName(inputValue.trim())
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          void surfaceVuexStore?.dispatch('inputDialog/setSubmitButtonDisable', {
            disable: inputValue.trim().length === 0,
          })
        },
      ],
    }
    void surfaceVuexStore?.dispatch('inputDialog/openInputDialog', payload, { root: true })
  }

  /* Submission Request */
  // State
  const isEnablingSubmissionRequest = ref(false)
  const isSubmissionRequestEnabled = ref(false)
  const submissionRequestLink = ref<string | null>(null)
  const submissionRequestRequiresLogin = ref<boolean | null>(null)
  const submissionRequestRedirectLocation = ref<string | null>(null)
  const disableSubmissionRequestTimer = ref<ReturnType<typeof setTimeout> | undefined>()
  const disableSubmissionRequestSnackbarUid = ref<string | null>(null)

  // Getters
  const submissionRequestSectionId = computed<number | null>(() => {
    if (!surfaceStore.isSubmissionRequest) return null

    const sectionIdParam = getSearchParam('section')
    if (sectionIdParam == null) return null

    // Validate that section exists
    const sectionId = parseInt(sectionIdParam)
    const section = surfaceSectionsStore.getSectionById(sectionId)
    return section == null ? null : sectionId
  })

  // Actions
  function setSubmissionRequestAttributes(linkAttributes: WallSubmissionRequestLink | null): void {
    submissionRequestLink.value = linkAttributes?.link ?? null
    submissionRequestRequiresLogin.value = linkAttributes?.requires_login ?? null
    submissionRequestRedirectLocation.value = linkAttributes?.redirect_location ?? null
  }

  async function fetchSubmissionRequestLink(): Promise<void> {
    // if user is not admin & not in submission request view, we perform early exit to avoid showing section breakout links
    if (
      (!isAppUsing('submissionRequest') || surfaceStore.isSectionBreakout || !surfacePermissionsStore.canIAdminister) &&
      !surfaceStore.isSubmissionRequest
    ) {
      return
    }

    try {
      const linkAttributes = await PadletApi.SubmissionRequest.fetch(surfaceStore.wallId)

      if (linkAttributes == null) {
        setSubmissionRequestAttributes(null)
        isSubmissionRequestEnabled.value = false

        if (surfaceStore.isSubmissionRequest) {
          // Redirect user if is in submissionRequestPage that no longer exists
          void navigateToSubmissionRequestDisabledPage()
        }

        return
      }
      if (surfaceStore.isSubmissionRequest && linkAttributes.requires_login && !isRegistered(surfaceStore.user)) {
        navigateTo(authWithReferrerUrl(currentUrl()))
        return
      }

      setSubmissionRequestAttributes(linkAttributes)
      isSubmissionRequestEnabled.value = true
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelFetchSubmissionRequestLink' })
    }
  }

  async function enableSubmissionRequest(): Promise<void> {
    // Clear any existing callbacks to disable
    if (disableSubmissionRequestTimer.value !== undefined) clearTimeout(disableSubmissionRequestTimer.value)
    globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')

    const linkAttributes = await PadletApi.SubmissionRequest.enable(surfaceStore.wallId)
    setSubmissionRequestAttributes(linkAttributes)
    isSubmissionRequestEnabled.value = true
  }

  async function disableSubmissionRequest(): Promise<void> {
    try {
      await PadletApi.SubmissionRequest.disable(surfaceStore.wallId)
      isSubmissionRequestEnabled.value = false
      setSubmissionRequestAttributes(null)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelDisableSubmissionRequest' })
    }
  }

  async function disableSubmissionRequestWithUndo(): Promise<void> {
    const SNACKBAR_DURATION = 3000

    disableSubmissionRequestSnackbarUid.value = globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Submission request link closed'),
      actionText: 'Undo',
      persist: true,
      actionTextActions: [
        () => {
          // revert and close snackbar
          isSubmissionRequestEnabled.value = true
          if (disableSubmissionRequestTimer.value !== undefined) clearTimeout(disableSubmissionRequestTimer.value)
          globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')
        },
      ],
    })

    // To hide Submission Request link first to give illusion of deletion
    isSubmissionRequestEnabled.value = false

    // Timeout to close snackbar and disable section breakout links
    disableSubmissionRequestTimer.value = setTimeout(() => {
      globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')
      void disableSubmissionRequest()
    }, SNACKBAR_DURATION)
  }

  function askToDisableSubmissionRequest(): void {
    void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
      ...CONFIRM_TURN_OFF,
      title: __('Turn off submission request link?'),
      body: __('Current link will no longer work. Turning link back on will create a new link.'),
      afterConfirmActions: [disableSubmissionRequestWithUndo],
    })
  }

  async function toggleSubmissionRequest(): Promise<void> {
    const originalValue = isSubmissionRequestEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSubmissionRequest.value) return

      if (isSubmissionRequestEnabled.value) {
        await askToDisableSubmissionRequest()
      } else {
        // Only show loading state if toggling to True
        isEnablingSubmissionRequest.value = true
        await enableSubmissionRequest()
        isEnablingSubmissionRequest.value = false
      }
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSubmissionRequest' })
      isSubmissionRequestEnabled.value = originalValue
    }
  }

  function getSubmissionRequestProps(
    payload: { requiresLogin?: boolean; redirectLocation?: string } = {},
  ): WallSubmissionRequestProps | null {
    const requiresLogin =
      payload.requiresLogin !== undefined ? payload.requiresLogin : submissionRequestRequiresLogin.value
    const redirectLocation =
      payload.redirectLocation !== undefined ? payload.redirectLocation : submissionRequestRedirectLocation.value

    if (requiresLogin == null || redirectLocation == null) return null

    return {
      requires_login: requiresLogin,
      redirect_location: redirectLocation,
    }
  }

  async function updateSubmissionRequestProps(
    oldProps: WallSubmissionRequestProps,
    newProps: WallSubmissionRequestProps,
  ): Promise<void> {
    try {
      await PadletApi.SubmissionRequest.update(surfaceStore.wallId, {
        requires_login: newProps.requires_login,
        redirect_location: newProps.redirect_location,
      })
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelUpdateSubmissionRequestProps' })
      submissionRequestRedirectLocation.value = oldProps.redirect_location
      submissionRequestRequiresLogin.value = oldProps.requires_login
    }
  }

  async function enqueueToggleSubmissionRequestRequiresLogin(): Promise<void> {
    if (submissionRequestRequiresLogin.value === null) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ requiresLogin: !submissionRequestRequiresLogin.value })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRequiresLogin.value = !submissionRequestRequiresLogin.value

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function enqueueUpdateSubmissionRequestRedirectLocation(redirectLocation: string): Promise<void> {
    if (submissionRequestRedirectLocation.value === null) return
    if (submissionRequestRedirectLocation.value === redirectLocation) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ redirectLocation })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRedirectLocation.value = redirectLocation

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function copySubmissionRequestLinkAndAlert(): Promise<void> {
    if (submissionRequestLink.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: submissionRequestLink.value,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  async function showPostPublishedSnackbar(): Promise<void> {
    void globalSnackbarStore.setSnackbar({
      message: __('Post published'),
      notificationType: SnackbarNotificationType.success,
    })
  }

  async function startNewSubmissionRequest(): Promise<void> {
    if (surfaceStore.isMap) {
      const attributes: { postCid?: Cid; sectionId?: Id } = {}
      if (submissionRequestSectionId.value !== null) {
        attributes.sectionId = submissionRequestSectionId.value
      }
      surfaceMapStore.startPickingLocation(attributes)
    } else {
      const attributes: PostAttributes = {}
      attributes.wallSectionId = submissionRequestSectionId.value ?? surfaceSectionsStore.defaultSectionId
      surfacePostsStore.startNewPost({ attributes })
    }

    void surfaceVuexStore?.dispatch('composerModal/expand')
  }

  async function executeSubmissionRequestPostSubmitAction(): Promise<void> {
    switch (submissionRequestRedirectLocation.value) {
      case SubmissionRequestRedirectLocation.Padlet:
        navigateTo(surfaceStore.links.show)
        break
      case SubmissionRequestRedirectLocation.Confirmation:
        void navigateToSubmissionRequestConfirmationPage()
        break
      case SubmissionRequestRedirectLocation.RequestAgain:
        void showPostPublishedSnackbar()
        void startNewSubmissionRequest()
        break
    }
  }

  async function copySubmissionRequestSectionLinkAndAlert(sectionId: number): Promise<void> {
    if (submissionRequestLink.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: transformUrl(submissionRequestLink.value, {
        search: { section: sectionId.toString() },
      }),
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  async function navigateToSubmissionRequestDisabledPage(): Promise<void> {
    navigateTo(getSubmissionRequestDisabledUrl())
  }

  async function navigateToSubmissionRequestConfirmationPage(): Promise<void> {
    if (submissionRequestLink.value !== null) {
      navigateTo(getSubmissionRequestConfirmationUrl(submissionRequestLink.value))
    }
  }

  return {
    /* Section Breakout */
    // State
    isEnablingSectionBreakout,
    isSectionBreakoutEnabled,
    sectionBreakoutLinks,
    sectionBreakoutIdentifier,

    // Actions
    enableGroupBySections,
    toggleSectionBreakout,
    fetchSectionBreakoutLinks,
    copySectionBreakoutLinkAndAlert,
    showSectionInputDialog,
    setSectionBreakoutLinksState,
    copySectionBreakoutPostLinkAndAlert,
    navigateToSectionBreakoutDisabledPage,

    /* Submission Request */
    // State
    isEnablingSubmissionRequest,
    isSubmissionRequestEnabled,
    submissionRequestLink,
    submissionRequestRequiresLogin,
    submissionRequestRedirectLocation,

    // Getters
    submissionRequestSectionId,

    // Actions
    fetchSubmissionRequestLink,
    setSubmissionRequestAttributes,
    toggleSubmissionRequest,
    enqueueToggleSubmissionRequestRequiresLogin,
    enqueueUpdateSubmissionRequestRedirectLocation,
    copySubmissionRequestLinkAndAlert,
    copySubmissionRequestSectionLinkAndAlert,
    startNewSubmissionRequest,
    executeSubmissionRequestPostSubmitAction,
    showPostPublishedSnackbar,
  }
})
